import {
  Box,
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { ByzMuiDateTimePicker } from "src/components/core-components/mui/inputs/ByzMuiDateTimePicker";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import { useTranslate } from "src/locales/use-locales";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import { useEffect, useState, useCallback } from "react";
import ByzMcParameterDetailSelect from "src/components/shared-components/ByzMcParameterDetailSelect";
import { useAppSelector } from "src/app/hooks";
import { useCardForm } from "src/hooks/useCardForm";
import { OtherPrivilegeGuard } from "src/utils/authorization/OtherPrivilegeGuard";
import { ByzMuiCheckBox } from "src/components/core-components/mui/ByzMuiCheckBox";

interface FallRiskItakiFormData {
  ID: string;
  FRI_ID: string;
  PROCESS_TIME: Date | null;
  NURSE_ID: string;
  CTIME: Date;
  CUSER: string;
  JUSTIFICATION_EVALUATION: string;
  AGE: string;
  STATE_CONSCIOUSNESS: string;
  FALL_HISTORY: string;
  DISEASES: string;
  WALKER_SUPPORT: string;
  EXCRETION: string;
  SIGHT_CONDITION: string;
  MEDICINE: string;
  MEDICINE_4DRUGUSES: string;
  MEDICINE_2RISKYDRUGS: string;
  MEDICINE_3OR1WEEKRISKY: string;
  EQUIPMENT: string;
  NURSE_NOTE: string;
  GENARAL_NOTE: string;
  CALC_VALUE: number;
}

interface FallRiskItakiProps {
  recordId?: string;
  patientId: number;
  onClose?: () => void;
  getComId: () => Promise<number | null>;
  comId: number;
  setComId: (comId: number) => void;
  getCardTableData: (comId: number) => Promise<string | "">;
  createTableMasterId: () => Promise<string | null>;
  save: (id: string) => Promise<void>;
  handleChange: (field: keyof FallRiskItakiFormData, value: any) => void;
  patientData: any;
  isSaving?: boolean;
  extraAccess?: boolean;
}

function FallRiskItakiCreate({
  recordId,
  save,
  handleChange: parentHandleChange,
  patientData,
  isSaving,
  extraAccess,
}: FallRiskItakiProps) {
  const { t } = useTranslate();
  const { user } = useAppSelector((state) => state.auth);

  const {
    formData,
    selectedMCPs,
    loading,
    handleChange,
    handleMCPChange,
    resetForm,
    cardData,
    isEditMode,
    getMCPParameterDetails,
    getScoreValue,
    mcpCacheLoaded,
  } = useCardForm({
    cardType: "FALL_RISK_ITAKI",
    recordId,
    user,
    onLoadRecord: (record) => {
      Object.entries(record).forEach(([key, value]) => {
        parentHandleChange(key as keyof FallRiskItakiFormData, value);
      });
    },
  });

  const typedFormData = formData as unknown as FallRiskItakiFormData;

  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    setIsInitialized(false);
  }, [patientData?.data?.id]);

  useEffect(() => {
    if (!isEditMode && patientData?.data && mcpCacheLoaded && !isInitialized) {

      resetForm();

      const patientInfo = patientData.data;
      const birthdate = patientInfo.birthdate;

      if (birthdate) {
        const birthDate = new Date(birthdate);
        const today = new Date();

        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();

        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }

        const realAge = Math.max(0, age);

        const ageDetails = getMCPParameterDetails(120);

        let selectedAgeDetail = null;

        if (realAge >= 80) {
          selectedAgeDetail = ageDetails.find(d => d.name.includes("80"));
        }
        else if (realAge >= 70) {
          selectedAgeDetail = ageDetails.find(d => d.name.includes("70"));
        }
        else if (realAge >= 60) {
          selectedAgeDetail = ageDetails.find(d => d.name.includes("60"));
        }
        else if (realAge >= 18) {
          selectedAgeDetail = ageDetails.find(d => d.name.includes("18") || d.name.includes("59"));
        }

        if (selectedAgeDetail) {
          handleMCPChange("AGE", selectedAgeDetail);
        }
        else if (ageDetails.length > 0) {
          handleMCPChange("AGE", ageDetails[0]);
        }
      }

      setIsInitialized(true);
    }

    else if (!isEditMode && !isInitialized) {
      resetForm();
      setIsInitialized(true);
    }

  }, [
    isEditMode,
    resetForm,
    patientData,
    mcpCacheLoaded,
    getMCPParameterDetails,
    handleMCPChange,
    isInitialized
  ]);

  useEffect(() => {
    Object.entries(formData).forEach(([key, value]) => {
      if (key !== "CALC_VALUE") {
        parentHandleChange(key as keyof FallRiskItakiFormData, value);
      }
    });
  }, [formData, parentHandleChange]);

  const [score, setScore] = useState(0);

  // Checkbox alanları için skor değerini MCP cache'den alan fonksiyon
  const getCheckboxScoreValue = useCallback((mcpName: string): number => {
    if (!cardData?.mcpConfig) return 0;

    const mcpConfig = cardData.mcpConfig[mcpName as keyof typeof cardData.mcpConfig];
    if (!mcpConfig) return 0;

    const mcpDetails = getMCPParameterDetails(mcpConfig.mcpId);
    // Checkbox alanları için genelde tek bir parametresi var ve value değeri var
    // İlk parametreyi ya da value > 0 olanı alalım
    const detail = mcpDetails.find(d => d.value && d.value > 0) || mcpDetails[0];
    return detail?.value || 0;
  }, [cardData, getMCPParameterDetails]);

  const handleScoreCalculate = useCallback(() => {
    if (!cardData) return;

    let calculatedScore = 0;

    // Score from MCP select fields
    calculatedScore += getScoreValue("AGE");
    calculatedScore += getScoreValue("STATE_CONSCIOUSNESS");
    calculatedScore += getScoreValue("DISEASES");
    calculatedScore += getScoreValue("WALKER_SUPPORT");
    calculatedScore += getScoreValue("SIGHT_CONDITION");
    calculatedScore += getScoreValue("EQUIPMENT");

    if (typedFormData.FALL_HISTORY === "1") calculatedScore += 3;
    if (typedFormData.EXCRETION === "1") calculatedScore += 1;
    if (typedFormData.MEDICINE_4DRUGUSES === "1") calculatedScore += getCheckboxScoreValue("MEDICINE_4DRUGUSES");
    if (typedFormData.MEDICINE_2RISKYDRUGS === "1") calculatedScore += getCheckboxScoreValue("MEDICINE_2RISKYDRUGS");
    if (typedFormData.MEDICINE_3OR1WEEKRISKY === "1") calculatedScore += getCheckboxScoreValue("MEDICINE_3OR1WEEKRISKY");

    setScore(calculatedScore);
    parentHandleChange("CALC_VALUE", calculatedScore);
  }, [
    cardData,
    getScoreValue,
    getCheckboxScoreValue,
    parentHandleChange,
    typedFormData
  ]);

  useEffect(() => {
    handleScoreCalculate();
  }, [handleScoreCalculate]);


  const renderMCPField = (
    fieldName: string,
    labelKey: string,
    mcpId: number,
    readOnly: boolean = false
  ) => (
    <Grid container alignItems="center" justifyContent="space-between">
      <Grid
        xs={12}
        sm={6}
        md={6}
        sx={{ borderBottom: "1px solid #E0E0E0" }}
      >
        <ByzMuiTypography sx={{ marginLeft: "30px" }}>
          {t(labelKey)}
        </ByzMuiTypography>
      </Grid>
      <Grid xs={12} sm={6} md={6}>
        {readOnly ? (
          <ByzMuiTextField
            sx={{ width: "100%" }}
            color="info"
            variant="outlined"
            value={selectedMCPs[fieldName]?.name || ""}
            cantChange
          />
        ) : (
          <ByzMcParameterDetailSelect
            title={t("select")}
            mcpId={mcpId}
            multiple={false}
            handleChange={(val) => {
              if (!Array.isArray(val)) {
                handleMCPChange(fieldName, val);
              }
            }}
            value={selectedMCPs[fieldName]}
            sx={{ width: "100%" }}
            cachedData={getMCPParameterDetails(mcpId)}
            cacheLoaded={mcpCacheLoaded}
          />
        )}
      </Grid>
    </Grid>
  );

  const renderCheckboxField = (
    fieldName: string,
    labelKey: string
  ) => {
    const isChecked = typedFormData[fieldName as keyof FallRiskItakiFormData] === "1" ||
      typedFormData[fieldName as keyof FallRiskItakiFormData] === 1;

    return (
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid
          xs={12}
          sm={6}
          md={6}
          sx={{ borderBottom: "1px solid #E0E0E0" }}
        >
          <ByzMuiTypography sx={{ marginLeft: "30px" }}>
            {t(labelKey)}
          </ByzMuiTypography>
        </Grid>
        <Grid xs={12} sm={6} md={6}>
          <ByzMuiCheckBox
            checked={isChecked}
            onChange={(e) => {
              handleChange(fieldName, e.target.checked ? "1" : "0");
            }}
            color="primary"
            disabled={!extraAccess}
          />
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      <Box sx={{ padding: "20px", width: "100%" }}>
        <Box mb={3}>
          <OtherPrivilegeGuard
            extraAccess={!!extraAccess}
            fallback={
              <ByzMuiSaveButton
                disabled
                tooltip={!extraAccess ? t("no-permission-for-this-action") : undefined}
              />
            }
          >
            <ByzMuiSaveButton
              onClick={() => save(typedFormData.FRI_ID)}
              loading={isSaving}
            />
          </OtherPrivilegeGuard>
        </Box>

        <Grid container spacing={2}>
          <Grid
            xs={12}
            sm={6}
            md={6}
            container
            direction={"column"}
            spacing={3}
          >
            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={true}>
                {t("fall-risk-itaki.title-two")}
              </ByzMuiTypography>
            </Grid>

            {/* Header Fields */}
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("datetime")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6} sx={{ width: "100%" }}>
                <ByzMuiDateTimePicker
                  sx={{ width: "100%" }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    }
                  }}
                  label={t("datetime")}
                  value={typedFormData.PROCESS_TIME}
                  onChange={(date) => handleChange("PROCESS_TIME", date)}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("createdby")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  color="info"
                  label={t("createdby")}
                  variant="outlined"
                  value={typedFormData.CUSER}
                  onChange={(e) => handleChange("CUSER", e.target.value)}
                  disabled
                />
              </Grid>
            </Grid>

            {renderMCPField("JUSTIFICATION_EVALUATION", "fall-risk-itaki.justification-evaluation", 129, !extraAccess)}

            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={true}>
                {t("fall-risk-itaki.risk-factor")}
              </ByzMuiTypography>
            </Grid>

            {renderMCPField("AGE", "fall-risk-itaki.age", 120, true)}
            {renderMCPField("STATE_CONSCIOUSNESS", "fall-risk-itaki.consciousness-status", 121, !extraAccess)}
            {renderCheckboxField("FALL_HISTORY", "fall-risk-itaki.fall-history-in-last-six-months")}
            {renderMCPField("DISEASES", "fall-risk-itaki.diseases", 123, !extraAccess)}
            {renderMCPField("WALKER_SUPPORT", "fall-risk-itaki.mobility", 124, !extraAccess)}
            {renderCheckboxField("EXCRETION", "fall-risk-itaki.has-urinary-or-fecal-incontinence")}
            {renderMCPField("SIGHT_CONDITION", "fall-risk-itaki.vision-status", 126, !extraAccess)}

            {/* Medicine Section */}
            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={true}>
                {t("fall-risk-itaki.medication-usage")}
              </ByzMuiTypography>
            </Grid>

            {renderCheckboxField("MEDICINE_4DRUGUSES", "fall-risk-itaki.has-polypharmacy")}
            {renderCheckboxField("MEDICINE_2RISKYDRUGS", "fall-risk-itaki.has-used-max-two-high-risk-medications-in-last-week")}
            {renderCheckboxField("MEDICINE_3OR1WEEKRISKY", "fall-risk-itaki.has-used-three-or-more-high-risk-medications-in-last-week")}

            {/* Equipment */}
            {renderMCPField("EQUIPMENT", "fall-risk-itaki.equipment-usage", 128, !extraAccess)}

            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={true}>
                {t("other")}
              </ByzMuiTypography>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("score")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  label={t("score")}
                  value={score}
                  disabled
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("note")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  label={t("note")}
                  value={typedFormData.NURSE_NOTE}
                  onChange={(e) => handleChange("NURSE_NOTE", e.target.value)}
                  disabled={!extraAccess}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}

export default FallRiskItakiCreate;
